
import { WrapResponse } from '@/types';
import { fun } from '@/lib/request';
import { api } from '@/lib/env';
import { StateSlice } from '@/stores/appStore';
import { snakeToCamel } from '@/lib/utils';
import { toast } from 'react-hot-toast';
import { simulateTyping } from '@/lib/simulateTyping';
import {
  Scaled
} from '@/components/pdf/types';
import { coordinatesToAnchors } from '@/lib/transfer';
import { captureTaskCompleted } from '@/lib/analytics/product-events';

type ChatStream = {
  eventSource: EventSource;
  stop: () => void;
};
// 对话事件请求
const sseRequestCompletion = ({
  url,
  params,
  onTyped,
  onCompleted,
  onResponse,
}: {
  url: string;
  params: Record<string, string>;
  onTyped: (msg: string, completed?: boolean) => void;
  // 开始响应，question
  onResponse: () => void;
  onCompleted: (completion: CompletionData) => void;
}): ChatStream => {
  const queryStr = Object.keys(params).map(key => (
    `${key}=${encodeURIComponent(params[key])}`
  )).join('&');
  const fullUrl = `${url}${queryStr ? '?' + queryStr : ''}`;
  const evtSource = new EventSource(fullUrl, {
    withCredentials: true,
  });

  const parse = (data: string): {
    code: number;
    content: string | Coordinates;
    [x: string]: any;
  } => {
    let result = {
      code: -1,
      content: data,
    };
    try {
      result = snakeToCamel(JSON.parse(data));
    } catch (error) {
    }
    return result;
  };
  const completion = {} as CompletionData;
  const setCompletion = (
    _key: (
      (keyof CompletionData) |
      'answer.coordinates' |
      'answer.error' |
      'answer.stoped'
    ),
    data: {
      code?: number;
      content: string | Coordinates;
      [x: string]: any;
    },
  ) => {
    const { code = -1, ...message } = data;
    const [
      key,
      aid,
    ] = _key.split('.') as [
      keyof CompletionData,
      string,
    ];
    completion[key] = completion[key] ?? {};
    // 接收到坐标信息，特殊处理一下
    if (aid === 'coordinates') {
      /**
       * 异步处理坐标转换
       * 
       * 由于后端改变了解析方式，无法再输出 pageSize 信息。
       * coordinatesToAnchors 函数使用 pdf.js 动态获取页面尺寸。
       * 在 SSE 流式响应中，没有 viewer 上下文，使用默认 A4 尺寸（612x792，72 DPI）。
       * 
       * 注意：这里先设置空数组，然后在异步完成后更新，避免阻塞主流程
       */
      coordinatesToAnchors(message.content || {}).then(anchors => {
        completion[key].anchors = anchors;
      }).catch(error => {
        console.error('Failed to convert coordinates to anchors:', error);
        completion[key].anchors = [];
      });
      // 临时设置空数组，避免类型错误
      completion[key].anchors = [];
    } else if (aid) {
      completion[key].status = aid as MessageStatus;
      completion[key].content = message.content as string || '';
      completion[key].anchors = [];
      if (key === 'answer') {
        completion[key].code = code;
      }
    } else {
      completion[key] = {
        ...completion[key],
        ...message,
        content: message.content || '',
        status: 'typing',
      } as Message;
    }
  };
  evtSource.addEventListener('select_info', (e) => {
    setCompletion('select', parse(e.data));
  });
  evtSource.addEventListener('question_info', (e) => {
    setCompletion('question', parse(e.data));
    // 兼容一下answer的输出
    setCompletion('answer', {
      code: 0,
      content: '',
    });
    onResponse();
  });
  evtSource.addEventListener('answer_info', (e) => {
    setCompletion('answer', parse(e.data));
  });
  evtSource.addEventListener('coordinates', (e) => {
    setCompletion('answer.coordinates', parse(e.data));
  });
  const typing = simulateTyping({
    onTyped: (typed, completed) => {
      onTyped(typed, completed);
      // 全部输出结束
      if (completed) {
        if (completion.answer) {
          let content = completion.answer.content;
          if (completion.answer.status === 'typing') {
            completion.answer.content = typed;
            completion.answer.status = 'completed';
          } else if (completion.answer.status === 'stoped') {
            content = (typed ? (typed + '\n\n') : '') + content;
            completion.answer.content = content;
          } else if (completion.answer.status === 'error') {
            completion.answer.content = content;
          }
        }
        onCompleted(completion);
      }
    },
  });
  evtSource.onmessage = (e) => {
    const { content } = parse(e.data);
    typing.accept(content as string);
  };
  // 接收到 close 信号才触发
  evtSource.addEventListener('close', (e) => {
    const data = parse(e.data);
    // 报错
    if (data.code !== 0) {
      setCompletion('answer.error', {
        ...data,
        content: data.message || data.content,
      });
    }
    typing.close();
    evtSource.close();
  });
  evtSource.onerror = (e) => {
    console.error("An error occurred while attempting to connect.", e);
  };
  return {
    eventSource: evtSource,
    stop: () => {
      setCompletion('answer.stoped', {
        content: '【AI 输出已停止】',
      });
      typing.close();
      evtSource.close();
    }
  };
};

export type MessageId = number;

export type ChatId = string;

/**
 * 坐标数据类型
 * 
 * @description
 * 由于后端改变了解析方式，无法再输出 pageSize 信息。
 * 前端使用 pdf.js 的 page.getViewport({ scale: 1 }) 动态获取页面尺寸。
 * 页面尺寸用于将相对坐标（points，范围 0-1）转换为 Scaled 对象。
 */
export type Coordinate = {
  /** 页面编号（从 0 开始） */
  pageNumber: number;
  /** 相对坐标点 [xMin, yMin, xMax, yMax]，范围在 0-1 之间 */
  points: [number, number, number, number];
};
export type Coordinates = Coordinate[][] | Record<string, Coordinate[]> | {};

export interface Anchor extends Scaled {
  anchorId: string;
}
export type Anchors = Anchor[];
export type Role = 'user' | 'assistant' | 'system' | 'select';
export type MessageStatus = 'error' | 'stoped' | 'completed' | 'typing';
export type Message = {
  logId: MessageId;
  role: Role;
  content: string | React.JSX.Element;
  createTime: string;
  status?: MessageStatus;
  anchors?: Anchors;
  code?: number;
};

type ChatData = {
  minId: MessageId;
  list: ({
    coordinates: Coordinates;
  } & Message)[];
};

export type Messages = Message[];

// type NonUndefined<T> = T extends undefined ? never : T;
// type ArrayElementType<ArrayType extends Array<T>, T = any> = ArrayType[number];
export type Tool = {
  toolKey: string;
  icon: string;
  tag: string;
  respContent: string;
};
export type Tools = Tool[];

type CompletionParams = {
  chatId: ChatId;
  content: string;
  bySelect?: {
    toolKey: Tool['toolKey'];
    content: string;
  };
  onResponse?: () => void;
}

type CompletionData = {
  select: Message;
  question: Message;
  answer: Message;
};

export type ChatSlice = {
  chats: Record<ChatId, Messages>;
  fetchChatLoading: boolean;
  fetchChatHistoryLoading: boolean;
  isFetchChatByHistory: boolean;
  fetchChat: (chatId: ChatId) => Promise<Messages>;
  fetchChatHistory: (chatId: ChatId) => Promise<Messages | null>;
  // 分别对应：['select', 'user', 'assistant']
  chatOnces: Record<string, [string, string, string] | []>;
  completionChatStream: (params: CompletionParams) => ChatStream | null;
};

export const createChatSlice: StateSlice<ChatSlice> = ((set, get) => ({
  chats: {},
  fetchChatLoading: false,
  fetchChatHistoryLoading: false,
  isFetchChatByHistory: false,
  fetchChat: async (chatId) => {
    if (get().fetchChatLoading) {
      return get().chats[chatId] || [];
    }
    set(state => {
      state.fetchChatLoading = true;
      state.isFetchChatByHistory = false;
    })
    try {
      const formData = new FormData();
      formData.append('chat_id', chatId);
      formData.append('page_size', '50');
      // 获取最新数据，max_log_id 从 -1 计数
      formData.append('max_log_id', '-1');
      const { data, code, message: msg } = (
        await fun.post<WrapResponse<ChatData>>(
          api('/chat/history', true),
          formData,
        )
      ).data;
      if (code === 0) {
        const chat = snakeToCamel<ChatData>(data);
        /**
         * 异步处理坐标转换
         * 
         * 由于后端改变了解析方式，无法再输出 pageSize 信息。
         * coordinatesToAnchors 函数使用 pdf.js 动态获取页面尺寸。
         * 在历史数据场景中，没有 viewer 上下文，使用默认 A4 尺寸（612x792，72 DPI）。
         */
        const messagesWithAnchors = await Promise.all(
          chat.list.map(async ({ coordinates, ...message }) => ({
            ...message,
            anchors: await coordinatesToAnchors(coordinates), // 没有 viewer，使用默认 A4 尺寸
          }))
        );
        set((state) => {
          const map: Record<string, Message> = {};
          ([] as Messages)
            .concat(
              state.chats[chatId] || []
            )
            .concat(messagesWithAnchors)
            .forEach((item) => {
              if (item?.logId) {
                map[item.logId] = item;
              }
            });
          state.chats[chatId] = Object.values(map);
        });
        return get().chats[chatId] || [];
      } else {
        toast.error(msg);
      }
      console.error('Error fetch chat:', msg);
    } catch (error) {
      console.error('Error fetch chat:', error);
    } finally {
      set(state => {
        state.fetchChatLoading = false;
      });
    }
    return get().chats[chatId] || [];
  },
  fetchChatHistory: async (chatId) => {
    if (get().fetchChatHistoryLoading) {
      return null;
    }
    set(state => {
      state.fetchChatHistoryLoading = true;
      state.isFetchChatByHistory = true;
    })
    try {
      const chats = get().chats;
      const existChat = chats[chatId];
      const formData = new FormData();
      formData.append('chat_id', chatId);
      formData.append('page_size', '50');
      if (existChat?.[0]) {
        // 从上次请求的最小请求ID请求，以追加数据
        formData.append('max_log_id', existChat[0].logId.toString());
      } else {
        // 第一次请求数据
        formData.append('max_log_id', '-1');
      }
      const { data, code, message: msg } = (
        await fun.post<WrapResponse<ChatData>>(
          api('/chat/history', true),
          formData,
        )
      ).data;
      if (code === 0) {
        const chat = snakeToCamel<ChatData>(data);
        if (chat.list.length) {
          /**
           * 异步处理坐标转换
           * 
           * 原因：由于后端改变了解析方式，无法再输出 pageSize 信息。
           * coordinatesToAnchors 函数现在需要异步从 PDF 文档中获取页面尺寸，
           * 或者在没有 viewer 的情况下使用默认值。
           * 
           * 注意：这里处理的是历史数据，可能没有 viewer 可用，函数会使用默认 A4 尺寸
           */
          const messagesWithAnchors = await Promise.all(
            chat.list.map(async ({ coordinates, ...message }) => ({
              ...message,
              anchors: await coordinatesToAnchors(coordinates),
            }))
          );
          set((state) => {
            const map: Record<string, Message> = {};
            ([] as Messages)
              .concat(messagesWithAnchors)
              .concat(
                state.chats[chatId] || []
              )
              .forEach((item) => {
                map[item.logId] = item;
              });
            state.chats[chatId] = Object.values(map);
          });
        }
        return get().chats[chatId] || [];
      } else {
        toast.error(msg);
      }
      console.error('Error fetch chat history:', msg);
      return null;
    } catch (error) {
      console.error('Error fetch chat history:', error);
      return null;
    } finally {
      set(state => {
        state.fetchChatHistoryLoading = false;
      });
    }
  },
  chatOnces: {},
  // completionChat: async ({ chatId, content }) => {
  //   set((state) => {
  //     state.chatOnce = [content, ''];
  //   });

  //   try {
  //     const formData = new FormData();
  //     formData.append('chat_id', chatId);
  //     formData.append('message', content);
  //     const { data, code, message: msg } = (
  //       await axios.post<WrapResponse<CompletionData>>(
  //         '/api/chat/completionChat',
  //         formData,
  //       )
  //     ).data;
  //     if (code === 0) {
  //       const completion = snakeToCamel<CompletionData>(data);

  //       await mockServerSentEvents(completion.answer.content, (content) => {
  //         // 逐字更新
  //         set((state) => {
  //           state.chatOnce[1] = content;
  //         });
  //       });

  //       // 更新历史
  //       set((state) => {
  //         const chat = state.chats[chatId];
  //         chat.push(completion.question, completion.answer);
  //       });
  //       return;
  //     } else {
  //       toast.error(msg);
  //     }
  //     console.error('Error fetch chat history:', msg);
  //     return;
  //   } catch (error) {
  //     console.error('Error fetch chat history:', error);
  //     return;
  //   } finally {
  //     set((state) => {
  //       state.chatOnce = [];
  //     });
  //   }
  // },
  completionChatStream: ({ chatId, content, bySelect, onResponse }) => {
    set((state) => {
      // 用户选中的、用户说的，AI回答的，异常报错
      state.chatOnces[chatId] = [bySelect?.content || '', content, ''];
    });

    try {
      const selectParams: Record<string, string> = bySelect ? {
        // 选中的内容
        message: bySelect.content,
        tool_key: bySelect.toolKey,
      } : {};
      // https://developer.mozilla.org/zh-CN/docs/Web/API/Server-sent_events/Using_server-sent_events
      const chatStream = sseRequestCompletion({
        url: api('/chat/completionChatStream', true),
        params: {
          chat_id: chatId.toString(),
          message: content,
          ...selectParams,
        },
        onTyped: (output) => {
          // 逐字更新
          set((state) => {
            const len = state.chatOnces[chatId].length;
            state.chatOnces[chatId][len - 1] = output;
          });
        },
        onResponse: () => {
          // 服务端有响应，刷新金币 
          get().refreshUserInfo();
          onResponse?.();
        },
        onCompleted: (completion) => {
          if (completion.answer.code === 5001) {
            get().setBalanceInsufficient(true);
          }
          if (completion.answer.status === 'completed' && completion.answer.code === 0) {
            captureTaskCompleted({
              task_type: 'paper_chat',
              used_selection: !!bySelect,
            }, get().user?.userId);
          }
          // 更新历史
          set((state) => {
            const chat = state.chats[chatId];
            chat.push(
              completion.select,
              completion.question,
              completion.answer,
            );
          });
          // // 获取列表最新数据
          // await get().fetchChat(chatId);
          set((state) => {
            state.chatOnces[chatId] = [];
          });
        }
      });
      return chatStream;
    } catch (error) {
      console.error('Error fetch chat history:', error);
      return null;
    }
  },
}));
